import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { BLOG_POSTS as LOCAL_POSTS } from '../data/blogPosts';
import { Clock, BookOpen, ArrowRight, WhatsappLogo } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch from API; if it fails (cold cache / network), fall back to local seed data
    axios.get(`${API}/blog`)
      .then(({ data }) => {
        // Map API format → local format compatible (use cover_image -> coverImage, etc.)
        const mapped = (data.posts || []).map(p => ({
          slug: p.slug,
          title: p.title,
          excerpt: p.excerpt,
          category: p.category,
          coverImage: p.cover_image,
          readMinutes: p.read_minutes,
          publishedAt: p.published_at,
        }));
        setPosts(mapped.length ? mapped : LOCAL_POSTS);
      })
      .catch(() => setPosts(LOCAL_POSTS))
      .finally(() => setLoading(false));
  }, []);

  const BLOG_POSTS = posts;

  return (
    <div className="min-h-screen bg-surface-secondary" data-testid="blog-page">
      <Navbar />
      <main className="pt-28 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <header className="text-center mb-12 max-w-3xl mx-auto">
            <p className="text-xs uppercase tracking-[0.2em] text-accent font-bold mb-2">Patient Resources</p>
            <h1 className="font-spectral text-4xl sm:text-5xl font-bold text-primary mb-4 leading-tight">
              Medical Second Opinion <span className="text-accent">Guide</span>
            </h1>
            <p className="text-secondary text-base sm:text-lg leading-relaxed">
              Trusted, plain-English articles to help Indian patients make confident health decisions — before surgery, cancer treatment, IVF, and more.
            </p>
          </header>

          {/* Featured (first post) */}
          {BLOG_POSTS[0] && (
            <Link
              to={`/blog/${BLOG_POSTS[0].slug}`}
              className="block mb-12 group"
              data-testid="featured-blog-post"
            >
              <article className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-surface rounded-3xl overflow-hidden border border-primary/10 hover:shadow-[0_20px_50px_rgb(0,0,0,0.1)] transition-all duration-300">
                <div className="relative aspect-[5/4] md:aspect-auto overflow-hidden">
                  <img
                    src={BLOG_POSTS[0].coverImage}
                    alt={BLOG_POSTS[0].title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="eager"
                  />
                  <span className="absolute top-4 left-4 bg-accent text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                    Featured
                  </span>
                </div>
                <div className="p-6 sm:p-8 flex flex-col justify-center">
                  <div className="flex items-center gap-3 text-xs text-secondary mb-3">
                    <span className="bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">{BLOG_POSTS[0].category}</span>
                    <span className="flex items-center gap-1"><Clock size={12} /> {BLOG_POSTS[0].readMinutes} min read</span>
                  </div>
                  <h2 className="font-spectral text-2xl sm:text-3xl font-bold text-primary mb-3 leading-tight group-hover:text-accent transition-colors">
                    {BLOG_POSTS[0].title}
                  </h2>
                  <p className="text-secondary text-sm sm:text-base leading-relaxed mb-4">
                    {BLOG_POSTS[0].excerpt}
                  </p>
                  <span className="inline-flex items-center gap-2 text-accent font-semibold text-sm">
                    Read article <ArrowRight size={16} weight="bold" className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </article>
            </Link>
          )}

          {/* Posts grid */}
          <h2 className="font-spectral text-2xl font-bold text-primary mb-6">All Articles</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {BLOG_POSTS.slice(1).map((post, idx) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                className="group bg-surface rounded-2xl overflow-hidden border border-primary/10 hover:shadow-[0_15px_30px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300"
                data-testid={`blog-card-${idx}`}
              >
                <article>
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={post.coverImage}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 text-xs text-secondary mb-2">
                      <span className="bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">{post.category}</span>
                      <span className="flex items-center gap-1"><Clock size={12} /> {post.readMinutes} min</span>
                    </div>
                    <h3 className="font-spectral text-lg font-semibold text-primary leading-snug mb-2 group-hover:text-accent transition-colors line-clamp-3">
                      {post.title}
                    </h3>
                    <p className="text-secondary text-sm leading-relaxed line-clamp-3">{post.excerpt}</p>
                  </div>
                </article>
              </Link>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="mt-16 bg-primary rounded-3xl p-8 sm:p-12 text-center text-white">
            <BookOpen size={40} weight="duotone" className="mx-auto mb-4 text-accent" />
            <h2 className="font-spectral text-2xl sm:text-3xl font-bold mb-3">Need an Expert Second Opinion?</h2>
            <p className="text-white/85 mb-6 max-w-xl mx-auto">
              Reading is helpful — but nothing replaces a written opinion from a specialist registered with the NMC or a State Medical Council who reviews your specific reports.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/register"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-accent text-white rounded-full font-semibold hover:bg-accent/90 transition-colors"
                data-testid="blog-cta-register"
              >
                Get a Second Opinion (₹1,500)
              </Link>
              <a
                href="https://wa.me/918840804798?text=Hi%2C%20I%20want%20to%20know%20more%20about%20Second%20Doctor%20Opinion"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-white/30 text-white rounded-full font-semibold hover:bg-white/10 transition-colors"
              >
                <WhatsappLogo size={20} weight="fill" /> Ask on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
